import React from 'react';
import styles from './Button.module.css';

const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  className = '',
  icon,
  fullWidth = false,
  ...props
}) => {
  const baseStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'var(--font-display)',
    fontWeight: 800,
    letterSpacing: '0.02em',
    borderRadius: 'var(--radius-md)',
    border: '2px solid transparent',
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    opacity: disabled || loading ? 0.5 : 1,
    transition: 'transform var(--transition-fast), box-shadow var(--transition-fast), filter var(--transition-fast), background var(--transition-fast), border-color var(--transition-fast)',
    outline: 'none',
    width: fullWidth ? '100%' : 'auto',
    gap: 'var(--space-2)',
    lineHeight: 1.2,
  };

  const variants = {
    primary: {
      background: 'var(--gradient-loud)',
      color: '#0a0810',
      boxShadow: 'var(--shadow-btn)',
    },
    secondary: {
      background: 'rgba(255, 255, 255, 0.04)',
      color: 'var(--color-text-primary)',
      border: '2px solid var(--color-border)',
      backdropFilter: 'blur(8px)',
    },
    danger: {
      background: 'linear-gradient(135deg, #ff4d6d, #c4004a)',
      color: '#fff',
    },
    ghost: {
      background: 'transparent',
      color: 'var(--loud-lime)',
      border: '2px solid transparent',
    },
    outline: {
      background: 'transparent',
      color: 'var(--loud-orange-hot)',
      border: '2px solid rgba(255, 90, 31, 0.55)',
    },
  };

  const sizes = {
    sm: { padding: '6px 12px', fontSize: 'var(--text-xs)', borderRadius: 'var(--radius-sm)' },
    md: { padding: '10px 18px', fontSize: 'var(--text-sm)' },
    lg: { padding: '14px 24px', fontSize: 'var(--text-base)', borderRadius: 'var(--radius-lg)' },
  };

  const variantStyle = variants[variant] || variants.primary;
  const sizeStyle = sizes[size] || sizes.md;

  const combinedStyle = {
    ...baseStyles,
    ...variantStyle,
    ...sizeStyle,
  };

  const variantClass = styles[variant] || styles.primary;
  const buttonClassName = [styles.btn, variantClass, className].filter(Boolean).join(' ');

  return (
    <button
      style={combinedStyle}
      className={buttonClassName}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <svg
          style={{ animation: 'spin 1s linear infinite', width: '16px', height: '16px' }}
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden
        >
          <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path
            style={{ opacity: 0.75 }}
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {icon && !loading && <span style={{ display: 'flex', alignItems: 'center' }}>{icon}</span>}
      {children}
    </button>
  );
};

export default Button;
